/**
 * Player profile — identity and lifetime record. (§9.1)
 *
 * One versioned localStorage key, `fg.profile.v1`, following the pattern
 * settings.ts and tournament-store.ts already established: parsing never
 * throws, a corrupt or future blob falls back to a fresh default rather than a
 * half-valid object, and a write failure (quota, private mode) is swallowed
 * because losing a stat must never take the menu down.
 *
 * ── Why identity lives here and not in the online layer ─────────────────────
 *
 * The online transport needs a stable player id and a display name. So does the
 * Profile screen. Putting them in the online layer would mean the profile
 * screen imports a networking module to learn the player's own name, and the id
 * would be reborn every time the transport was recreated — so a reconnecting
 * player would arrive in their own lobby as a stranger. The id is generated
 * once, persisted, and is the same value for the life of the browser profile.
 *
 * The id is local-only and carries no personal data: it is random, not derived
 * from anything about the machine or the player.
 *
 * ── What is stored vs derived ───────────────────────────────────────────────
 *
 * Stored: identity, and counters that are outcomes (played/won/drawn/lost,
 * goals). Derived: win rate, goal difference, form — computed on read, because
 * a stored derived value is just a second source of truth waiting to disagree
 * with the first.
 */

const PROFILE_KEY = 'fg.profile.v1';

export interface PlayerProfile {
  /** Stable random id. Never leaves the device except as a room participant id. */
  id: string;
  displayName: string;
  /** Matches finished, by outcome. Abandoned matches are not counted. */
  played: number;
  won: number;
  drawn: number;
  lost: number;
  goalsFor: number;
  goalsAgainst: number;
  /** Team id last taken into a match, so setup screens can preselect it. */
  favouriteTeamId: string | null;
}

export const DEFAULT_DISPLAY_NAME = 'Player';

/**
 * 12 hex characters of randomness.
 *
 * `crypto.randomUUID` is not used because it is unavailable on insecure
 * origins, which is exactly where a dev build runs. This needs to be unique
 * among a handful of players in a room, not globally unique.
 */
function makeId(): string {
  const bytes = new Uint8Array(6);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export function defaultProfile(): PlayerProfile {
  return {
    id: makeId(),
    displayName: DEFAULT_DISPLAY_NAME,
    played: 0,
    won: 0,
    drawn: 0,
    lost: 0,
    goalsFor: 0,
    goalsAgainst: 0,
    favouriteTeamId: null,
  };
}

/** Display names are shown to other players, so they are bounded and trimmed. */
export const MAX_DISPLAY_NAME = 16;

export function sanitiseDisplayName(raw: string): string {
  // Two different things are being removed here and they are not the same:
  //
  //   Whitespace controls (newline, tab) are whitespace the player typed. They
  //   still break lobby layout, so they collapse to a single space: a name typed
  //   across two lines becomes "Raj Kumar", which is what was meant, rather than
  //   "RajKumar".
  //
  //   Non-whitespace controls are junk with no intended reading, so they are
  //   dropped outright rather than turned into spacing that was never there.
  //
  // Escaping is not the concern: names render via textContent everywhere.
  const cleaned = [...raw]
    .filter((c) => {
      if (/\s/.test(c)) return true;
      const cp = c.codePointAt(0) ?? 0;
      return cp >= 0x20 && cp !== 0x7f;
    })
    .join('')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_DISPLAY_NAME);
  return cleaned.length > 0 ? cleaned : DEFAULT_DISPLAY_NAME;
}

function num(v: unknown, fallback = 0): number {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 ? Math.floor(v) : fallback;
}

/** Pure and exported so it is testable without localStorage. */
export function parseProfile(json: string | null | undefined): PlayerProfile {
  if (typeof json !== 'string' || json.length === 0) return defaultProfile();
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return defaultProfile();
  }
  if (typeof raw !== 'object' || raw === null) return defaultProfile();
  const o = raw as Record<string, unknown>;
  const id = typeof o['id'] === 'string' && o['id'].length > 0 ? o['id'] : makeId();
  const name = typeof o['displayName'] === 'string'
    ? sanitiseDisplayName(o['displayName'])
    : DEFAULT_DISPLAY_NAME;
  const fav = typeof o['favouriteTeamId'] === 'string' ? o['favouriteTeamId'] : null;
  return {
    id,
    displayName: name,
    played: num(o['played']),
    won: num(o['won']),
    drawn: num(o['drawn']),
    lost: num(o['lost']),
    goalsFor: num(o['goalsFor']),
    goalsAgainst: num(o['goalsAgainst']),
    favouriteTeamId: fav,
  };
}

export function loadProfile(): PlayerProfile {
  try {
    if (typeof localStorage === 'undefined') return defaultProfile();
    return parseProfile(localStorage.getItem(PROFILE_KEY));
  } catch {
    return defaultProfile();
  }
}

export function saveProfile(p: PlayerProfile): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(PROFILE_KEY, JSON.stringify(p));
  } catch {
    // Quota or private mode. A lost stat is not worth breaking the menu over.
  }
}

/**
 * Load, mutate, save. The only way profile fields change, so no caller can
 * write a profile it did not first read and accidentally reset the counters.
 */
export function updateProfile(patch: Partial<PlayerProfile>): PlayerProfile {
  const next = { ...loadProfile(), ...patch };
  saveProfile(next);
  return next;
}

/** Record a finished match. Goals are the player's own scoreline, in order. */
export function recordMatchResult(scored: number, conceded: number): PlayerProfile {
  const p = loadProfile();
  const next: PlayerProfile = {
    ...p,
    played: p.played + 1,
    won: p.won + (scored > conceded ? 1 : 0),
    drawn: p.drawn + (scored === conceded ? 1 : 0),
    lost: p.lost + (scored < conceded ? 1 : 0),
    goalsFor: p.goalsFor + Math.max(0, Math.floor(scored)),
    goalsAgainst: p.goalsAgainst + Math.max(0, Math.floor(conceded)),
  };
  saveProfile(next);
  return next;
}

/** Derived on read — see the header note on why these are not stored. */
export function winRate(p: PlayerProfile): number {
  return p.played === 0 ? 0 : p.won / p.played;
}

export function goalDifference(p: PlayerProfile): number {
  return p.goalsFor - p.goalsAgainst;
}
